import { Tooltip } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getShowStandardModal } from "src/common/state/slice/modal/modalSlice";
import ButtonSquareWithDropdownMenu from "../Button/ButtonSquareWithDropdownMenu";
import { GoBackButton } from "../GoBackButton/GoBackButton";
import Breadcrumb from "./Breadcrumb";
import NavigationTabs from "./NavigationTabs";
import {
  fetchProjectGroupsWithProjects,
  getSearchState,
  getSelectedProjectGroup,
  setSearchPage,
  setSearchQuery,
  setSearchSort,
} from "src/features/projectGroups/state/projectGroupsSlice";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import ProjectGroupSearchBar from "./ProjectGroupSearchBar";
import ProjectGroupSort, { DropdownOption } from "../Input/SortableDropdown";
import { useAppDispatch } from "src/common/state/hooks";
import { PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID } from "src/features/projectGroups/components/ProjectGroupList";
import { Checkbox } from "../Input";
import { RootState } from "src/common/state/store";
import { loadingSelector, LoadingState } from "src/common/state/slice/appStateSlice";

interface NavigationProps {
  tabs?: any;
  buttons?: React.ReactNode;
  title: string | React.JSX.Element;
  isProjectView?: boolean;
  isProjectGroupView?: boolean;
  titles?: string;
  tagsBar?: React.ReactNode;
  showGoBackButton?: boolean;
  showSearchBar?: boolean;
  backDepth?: number;
  backCustomRedirectTo?: string;
  isFilteredByPublishedProjects?: boolean;
  setIsFilteredByPublishedProjects?: (val: boolean) => void;
}

const Navigation = ({
  tabs,
  buttons,
  tagsBar,
  title,
  isProjectView = false,
  isProjectGroupView = false,
  showGoBackButton = false,
  showSearchBar = true,
  backDepth = 2,
  backCustomRedirectTo = "",
  isFilteredByPublishedProjects = false,
  setIsFilteredByPublishedProjects = () => null,
}: NavigationProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();

  const pathArray = pathname.split("/").filter(Boolean);

  const forStatistics = pathArray[0] === "statistics";
  const forDisplayProjectGroup = pathArray[0] === "courses" && pathArray.length === 1;

  const currentProjectGroup = useSelector(getSelectedProjectGroup);
  const projectGroupSearch = useSelector(getSearchState);
  const currentUser = useSelector(getCurrentUser);
  const isEnduser = currentUser?.role?.name === "Enduser";
  const { projectGroupListLoadingStatus } = useSelector((state: RootState) => {
    return {
      projectGroupListLoadingStatus: loadingSelector(
        state,
        PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
      ),
    };
  });

  const isGraphNodePath = /^\/?courses\/\d+\/list\/\d+\/graph\/node\/[^/]+\/\d+\/?$/.test(pathname);
  const showStandardModal = useSelector(getShowStandardModal);
  const isInsideModalGallery =
    showStandardModal.show === true && showStandardModal.id === "modal-gallery";
  const isInsideGallery = isInsideModalGallery || pathArray[0] === "medias";

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Track if we were on the courses list page
  const wasOnCoursesListRef = useRef(forDisplayProjectGroup);

  // Clear search text when leaving the courses list page (preserve page/limit for back nav)
  const queryClearedRef = useRef(false);
  useEffect(() => {
    if (wasOnCoursesListRef.current && !forDisplayProjectGroup) {
      dispatch(setSearchQuery(""));
      queryClearedRef.current = true;
    }
    wasOnCoursesListRef.current = forDisplayProjectGroup;
  }, [dispatch, forDisplayProjectGroup]);

  const prevPathnameRef = useRef(pathname);

  useEffect(() => {
    // Skip fetching project groups for endusers - they don't have permission and don't need this data
    // Also skip if user is not loaded yet to avoid race condition
    if (!currentUser || isEnduser) return;

    const prevPathname = prevPathnameRef.current;
    prevPathnameRef.current = pathname;

    // Returning to /courses from a child route (e.g. /courses/123/list) — restore saved page
    const isReturningToCoursesList = forDisplayProjectGroup && prevPathname.startsWith("/courses/");

    const page = isReturningToCoursesList ? projectGroupSearch.page ?? 0 : 0;

    // If the clear-search effect fired in this same render, the closure still holds the
    // stale query. Read the ref so we don't send a fetch with the old search term.
    const query = queryClearedRef.current ? "" : projectGroupSearch.query;
    queryClearedRef.current = false;

    dispatch(
      fetchProjectGroupsWithProjects({
        start: page * (projectGroupSearch.limit ?? 5),
        limit: projectGroupSearch.limit,
        sort: projectGroupSearch.sort,
        query,
        filterByPublishedProject: isFilteredByPublishedProjects,
        componentId: PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
      }),
    ).then(() => {
      dispatch(setSearchPage(page));
    });
  }, [
    dispatch,
    isFilteredByPublishedProjects,
    projectGroupSearch.limit,
    projectGroupSearch.sort,
    pathname,
    isInsideModalGallery,
    currentUser,
    isEnduser,
  ]);

  const handleDropdownToggle = useCallback((isOpen: boolean) => {
    setIsDropdownOpen(isOpen);
  }, []);

  const handleSortDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === "0") return;

    dispatch(setSearchSort(value));
    dispatch(setSearchPage(0));
    // setSearchSort clears the query when sort changes — match that here
    // instead of using projectGroupSearch.query which is stale from the closure
    dispatch(
      fetchProjectGroupsWithProjects({
        start: 0,
        limit: projectGroupSearch.limit,
        sort: value,
        filterByPublishedProject: isFilteredByPublishedProjects,
        componentId: PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
      }),
    );
  };

  const sortDropdownOptions: DropdownOption[] = [
    {
      value: "0",
      optionText: `${t("pages.gallery.filters.orderBy")}...`,
    },
    { value: "updated_at_ts_desc", optionText: t("general.date_desc") },
    { value: "updated_at_ts_asc", optionText: t("general.date_asc") },
    { value: "name_asc", optionText: t("general.alphanum_asc") },
    { value: "name_desc", optionText: t("general.alphanum_desc") },
  ];

  return (
    <div
      id="navbar"
      className={`sticky top-0 w-full block bg-slate-100 z-50 ${
        tabs ? "" : "border-b border-gray-200"
      }`}
    >
      <div className="relative flex mx-6 pt-4 justify-between mb-2 h-2/3">
        <div className="block">
          <Breadcrumb titles={title} />
          <div className="flex items-center">
            {showGoBackButton && (
              <div className="onboardingHowToAddScene-step-5">
                <GoBackButton
                  className="min-w-0 mr-2"
                  depth={backDepth}
                  backCustomRedirectTo={backCustomRedirectTo}
                />
              </div>
            )}
            <h3
              className={`${tabs ? "mt-2 mb-2" : "py-4"} text-3xl font-medium cursor-default ${
                isProjectGroupView && currentProjectGroup ? "text-green-500" : ""
              }`}
              style={{ caretColor: "transparent" }}
            >
              {isGraphNodePath && isInsideModalGallery ? "" : title}
            </h3>
            <div className="tagsBar">{tagsBar}</div>

            {isProjectGroupView && (
              <Tooltip title={t("tooltip.modifyCourse")} disableHoverListener={isDropdownOpen}>
                <div className="ml-1">
                  <ButtonSquareWithDropdownMenu
                    options="projectGroup"
                    onOpenChange={handleDropdownToggle}
                  />
                </div>
              </Tooltip>
            )}

            {isProjectView && (
              <Tooltip title={t("tooltip.modifyModule")} disableHoverListener={isDropdownOpen}>
                <div className="ml-1">
                  <ButtonSquareWithDropdownMenu
                    options="project"
                    onOpenChange={handleDropdownToggle}
                  />
                </div>
              </Tooltip>
            )}
          </div>
        </div>

        {showSearchBar && (
          <div
            className={`absolute right-6 ${
              forStatistics ? "top-[-1.5vh]" : isInsideGallery ? "lg:top-8" : "lg:top-4"
            } flex items-center min-w-[150px] flex-col`}
          >
            {forDisplayProjectGroup && (
              <div className="relative flex mr-16 mt-14 w-[39.2rem]">
                <ProjectGroupSearchBar
                  className="mr-2"
                  isFilteredByPublishedProjects={isFilteredByPublishedProjects}
                />
                <ProjectGroupSort
                  options={sortDropdownOptions}
                  search={projectGroupSearch}
                  handleChange={handleSortDropdownChange}
                  disabled={projectGroupListLoadingStatus == LoadingState.Loading}
                />
                <div className="flex ml-2 w-[10rem]">
                  <Checkbox
                    checked={isFilteredByPublishedProjects}
                    label={t("pages.projectGroups.filters.publishedGroups")}
                    onClick={(e) => {
                      setIsFilteredByPublishedProjects((e.target as any).checked);
                    }}
                    disabled={projectGroupListLoadingStatus == LoadingState.Loading}
                  />
                </div>
              </div>
            )}
            {buttons}
          </div>
        )}
      </div>
      {tabs && <NavigationTabs tabs={tabs} />}
    </div>
  );
};

export default Navigation;
